<template>
  <TransitionRoot as="template" :show="!!show && !!managedOrder && !showInvoicesModal">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-10">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-xl">
              <form class="flex h-full flex-col shellPanel">
                <!-- HEADER -->
                <div class="shrink-0 shellHeader">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold text-white tracking-tight">
                        Edytuj zamówienie paliwowe
                      </DialogTitle>

                      <div class="mt-2 flex flex-wrap items-center gap-2">
                        <span class="headerPill">
                          ID:
                          <span class="font-semibold text-white ml-1">
                            {{ managedOrder?.id || managedOrder?._id }}
                          </span>
                        </span>

                        <span
                          v-if="clientSource === 'existing'"
                          class="headerPill headerPillBlue"
                        >
                          Klient systemowy
                        </span>

                        <span
                          v-else
                          class="headerPill"
                        >
                          Klient ręczny
                        </span>
                      </div>

                      <p class="mt-3 text-sm text-slate-300">
                        Edytuj dane zamówienia, adres dostawy, paliwo i status realizacji.
                      </p>
                    </div>

                    <button
                      type="button"
                      class="iconBtn shrink-0"
                      @click.prevent="emitClose"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto space-y-4 shellBody">
                  <!-- KLIENT -->
                  <section class="sectionCard">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle">Klient</div>
                        <div class="sectionSub">
                          Dane klienta przypisanego do tego zamówienia.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody">
                      <div v-if="clientSource === 'existing'">
                        <div class="infoBox">
                          <div class="infoLabel">Typ klienta</div>
                          <div class="infoValue">Klient systemowy</div>
                        </div>

                        <div class="mt-4 dataPreview">
                          <div class="previewBlock">
                            <div class="previewLabel">Dane fakturowe</div>
                            <div class="previewMain">{{ managedOrder?.client?.invoiceAddress?.name || '—' }}</div>
                            <div class="previewText">
                              {{ managedOrder?.client?.invoiceAddress?.address || '—' }},
                              {{ managedOrder?.client?.invoiceAddress?.postCode || '—' }}
                              {{ managedOrder?.client?.invoiceAddress?.city || '' }}
                            </div>
                            <div class="previewText">
                              NIP: {{ managedOrder?.client?.invoiceAddress?.nip || '—' }}
                            </div>
                          </div>

                          <div class="previewBlock">
                            <div class="previewLabel">Kontakt</div>
                            <div class="previewText">
                              E-mail: {{ managedOrder?.client?.contact?.email || '—' }}
                            </div>
                            <div class="previewText">
                              Telefon: {{ managedOrder?.client?.contact?.phone || '—' }}
                            </div>
                          </div>
                        </div>

                        <div class="mt-4" v-if="canUpdateClientFromSystem">
                          <button
                            type="button"
                            @click.prevent="toggleUseUpdatedClient"
                            class="btnSecondary"
                          >
                            <span v-if="!useUpdatedSystemClient">Zaktualizuj klienta z UsersData</span>
                            <span v-else>Wycofaj aktualizację klienta</span>
                          </button>

                          <p v-if="useUpdatedSystemClient" class="fieldHint mt-2">
                            Przy zapisie dane klienta w zamówieniu zostaną podmienione na aktualne z
                            <span class="font-semibold text-slate-200">UsersData</span>.
                          </p>
                        </div>

                        <p v-else class="fieldHint mt-4">
                          Dane klienta w zamówieniu są zgodne z aktualnymi danymi w bazie użytkowników.
                        </p>

                        <!-- RATING PŁATNICZY -->
                        <div class="mt-4 ratingCard">
                          <div class="ratingTop">
                            <div class="min-w-0">
                              <div class="ratingHeading">Rating płatniczy</div>
                              <div class="ratingSub" v-if="!clientInvoicesLoading && !clientRating.hasHistory">
                                Rating zostanie wyliczony po pojawieniu się historii faktur.
                              </div>
                              <div class="ratingSub" v-else-if="clientInvoicesLoading">
                                Pobieranie historii faktur…
                              </div>
                              <div class="ratingSub" v-else>
                                Oparty na terminowości, liczbie i kwocie zaległości.
                              </div>
                            </div>

                            <button
                              type="button"
                              class="ratingBtn shrink-0"
                              :disabled="clientInvoicesLoading"
                              @click.prevent="showInvoicesModal = true"
                            >
                              <span v-if="clientInvoicesLoading">Wczytywanie…</span>
                              <span v-else>Zobacz faktury ({{ clientInvoices.length }})</span>
                            </button>
                          </div>

                          <div class="ratingStarsRow">
                            <div class="starsWrap" :class="{ 'starsWrap--empty': !clientRating.hasHistory }">
                              <svg
                                v-for="n in 5"
                                :key="n"
                                viewBox="0 0 20 20"
                                class="starIcon"
                                :class="{ 'starIcon--on': clientRating.hasHistory && n <= (clientRating.paymentRatingStars || 0) }"
                                aria-hidden="true"
                              >
                                <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                              </svg>
                            </div>
                            <div class="ratingValue">
                              <span v-if="clientInvoicesLoading">—</span>
                              <span v-else-if="!clientRating.hasHistory">Brak ratingu</span>
                              <span v-else>
                                {{ clientRating.paymentRatingStars }}/5
                                <span class="ratingValueHint">({{ clientRating.paymentRatingScore }} pkt)</span>
                              </span>
                            </div>
                          </div>

                          <div class="ratingGrid">
                            <div class="metricBox">
                              <div class="metricLabel">Nieopłacone faktury</div>
                              <div class="metricValue">
                                {{ clientRating.hasHistory ? clientRating.unpaidInvoicesCount : '—' }}
                              </div>
                              <div class="metricSub" v-if="clientRating.hasHistory && clientRating.overdueInvoicesCount > 0">
                                w tym {{ clientRating.overdueInvoicesCount }} po terminie
                              </div>
                            </div>
                            <div class="metricBox">
                              <div class="metricLabel">Kwota przeterminowana</div>
                              <div
                                class="metricValue"
                                :class="{ 'metricValue--danger': clientRating.hasHistory && clientRating.overdueAmount > 0 }"
                              >
                                {{ clientRating.hasHistory ? formatAmountPLN(clientRating.overdueAmount) : '—' }}
                              </div>
                            </div>
                            <div class="metricBox">
                              <div class="metricLabel">Średnie opóźnienie</div>
                              <div class="metricValue">
                                {{ clientRating.hasHistory ? formatAverageDelay(clientRating.averageDelayDays) : '—' }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- klient ręczny -->
                      <div v-else class="gridGap">
                        <p class="fieldHint">
                          Klient spoza systemu – możesz edytować wszystkie dane klienta.
                        </p>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Nazwa firmy*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.name"
                            type="text"
                            maxlength="100"
                            :disabled="cancelOrderOnSave"
                            :class="fieldClass(errors.clientName)"
                            placeholder="np. WB Tank Sp. z o.o."
                          />
                          <p v-if="errors.clientName" class="fieldError">Podaj nazwę firmy.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">NIP*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.nip"
                            type="text"
                            maxlength="20"
                            :disabled="cancelOrderOnSave"
                            :class="fieldClass(errors.clientNip)"
                            placeholder="np. 8421761290"
                          />
                          <p v-if="errors.clientNip" class="fieldError">Podaj NIP.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Adres*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.address"
                            type="text"
                            maxlength="100"
                            :disabled="cancelOrderOnSave"
                            :class="fieldClass(errors.clientAddress)"
                            placeholder="np. ul. Targowa 9"
                          />
                          <p v-if="errors.clientAddress" class="fieldError">Podaj adres.</p>
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div class="fieldWrap">
                            <label class="fieldLabel">Kod pocztowy*</label>
                            <input
                              v-model="form.clientManual.invoiceAddress.postCode"
                              type="text"
                              maxlength="20"
                              :disabled="cancelOrderOnSave"
                              :class="fieldClass(errors.clientPostCode)"
                              placeholder="np. 63-500"
                            />
                            <p v-if="errors.clientPostCode" class="fieldError">Podaj kod pocztowy.</p>
                          </div>

                          <div class="fieldWrap">
                            <label class="fieldLabel">Miejscowość*</label>
                            <input
                              v-model="form.clientManual.invoiceAddress.city"
                              type="text"
                              maxlength="60"
                              :disabled="cancelOrderOnSave"
                              :class="fieldClass(errors.clientCity)"
                              placeholder="np. Ostrzeszów"
                            />
                            <p v-if="errors.clientCity" class="fieldError">Podaj miejscowość.</p>
                          </div>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">E-mail kontaktowy*</label>
                          <input
                            v-model="form.clientManual.contact.email"
                            type="email"
                            maxlength="80"
                            :disabled="cancelOrderOnSave"
                            :class="fieldClass(errors.clientEmail)"
                            placeholder="np. biuro@firma.pl"
                          />
                          <p v-if="errors.clientEmail" class="fieldError">Podaj poprawny adres e-mail.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Telefon kontaktowy*</label>
                          <input
                            v-model="form.clientManual.contact.phone"
                            type="text"
                            maxlength="20"
                            :disabled="cancelOrderOnSave"
                            :class="fieldClass(errors.clientPhone)"
                            placeholder="np. 600700800"
                          />
                          <p v-if="errors.clientPhone" class="fieldError">Podaj numer telefonu (min. 5 znaków).</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- ADRES DOSTAWY -->
                  <section class="sectionCard">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle">Adres dostawy</div>
                        <div class="sectionSub">
                          Edytuj miejsce, region oraz preferowany termin dostawy.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody gridGap">
                      <!-- Selektor zapisanego adresu -->
                      <div class="fieldWrap" v-if="systemUserData?.deliveryAddresses?.length > 0 && !cancelOrderOnSave">
                        <label class="fieldLabel">Wybierz adres dostawy</label>
                        <select
                          :value="selectedDeliveryAddressIndex"
                          @change="selectDeliveryAddress($event.target.value === '' ? null : Number($event.target.value))"
                          class="fieldInput"
                        >
                          <option :value="''">Wpisz ręcznie</option>
                          <option
                            v-for="(addr, idx) in systemUserData.deliveryAddresses"
                            :key="idx"
                            :value="idx"
                          >
                            {{ addr.label ? addr.label + ' — ' : '' }}{{ addr.city }}, {{ addr.address }}{{ addr.regionName ? ' (' + addr.regionName + ')' : '' }}
                          </option>
                        </select>
                      </div>

                      <p v-if="selectedDeliveryAddressIndex !== null && !cancelOrderOnSave" class="pickedFromListHint">
                        Wybrany z listy. Zmień przez „Wpisz ręcznie" powyżej.
                      </p>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Adres*</label>
                        <input
                          v-model="form.deliveryAddress.address"
                          type="text"
                          maxlength="100"
                          :disabled="cancelOrderOnSave || selectedDeliveryAddressIndex !== null"
                          :class="fieldClass(errors.deliveryAddressField)"
                          placeholder="np. Targowa 9"
                        />
                        <p v-if="errors.deliveryAddressField" class="fieldError">Podaj adres dostawy.</p>
                      </div>

                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Kod pocztowy*</label>
                          <input
                            v-model="form.deliveryAddress.postCode"
                            type="text"
                            maxlength="20"
                            :disabled="cancelOrderOnSave || selectedDeliveryAddressIndex !== null"
                            :class="fieldClass(errors.deliveryPostCode)"
                            placeholder="np. 63-500"
                          />
                          <p v-if="errors.deliveryPostCode" class="fieldError">Podaj kod pocztowy.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Miejscowość*</label>
                          <input
                            v-model="form.deliveryAddress.city"
                            type="text"
                            maxlength="60"
                            :disabled="cancelOrderOnSave || selectedDeliveryAddressIndex !== null"
                            :class="fieldClass(errors.deliveryCity)"
                            placeholder="np. Ostrzeszów"
                          />
                          <p v-if="errors.deliveryCity" class="fieldError">Podaj miejscowość.</p>
                        </div>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Region dostawy</label>
                        <select
                          v-model="form.deliveryAddress.regionName"
                          :disabled="cancelOrderOnSave || selectedDeliveryAddressIndex !== null"
                          class="fieldInput"
                        >
                          <option :value="''">Nie przypisano</option>
                          <option
                            v-for="region in regions"
                            :key="region.id"
                            :value="region.name"
                          >
                            {{ region.name }} – {{ region.description }}
                          </option>
                        </select>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Weryfikacja adresu (Google Maps)</label>
                        <div v-if="deliveryVerification.loading" class="addrVerifyBox addrVerifyLoading">
                          <div class="flex items-center gap-2">
                            <svg class="animate-spin h-4 w-4 text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            <span>Weryfikowanie adresu w Google Maps…</span>
                          </div>
                        </div>
                        <div v-else-if="deliveryVerification.error" class="addrVerifyBox addrVerifyError">
                          <p class="font-semibold">Nie udało się zweryfikować adresu</p>
                          <p class="mt-1 text-xs opacity-90">{{ deliveryVerification.error }}</p>
                        </div>
                        <div v-else-if="deliveryVerification.coordinates" class="addrVerifyBox addrVerifySuccess">
                          <p class="text-xs font-semibold text-emerald-300">Adres zweryfikowany ✓</p>
                          <p class="mt-1 text-xs text-slate-200" v-if="deliveryVerification.formattedAddress">
                            Google: <span class="font-semibold text-white">{{ deliveryVerification.formattedAddress }}</span>
                          </p>
                          <p class="mt-0.5 text-[11px] text-slate-400">
                            Koordynaty: {{ deliveryVerification.coordinates.latitude }}, {{ deliveryVerification.coordinates.longitude }}
                          </p>
                          <div v-if="deliveryVerification.mapEmbedUrl" class="mt-3 overflow-hidden" style="border-radius: 14px; border: 1px solid rgba(71,85,105,0.6);">
                            <iframe
                              :src="deliveryVerification.mapEmbedUrl"
                              width="100%"
                              height="220"
                              style="border: 0; display: block;"
                              loading="lazy"
                              referrerpolicy="no-referrer-when-downgrade"
                              allowfullscreen
                            ></iframe>
                          </div>
                          <p class="mt-2 text-[11px] text-slate-400">
                            Sprawdź na mapie czy pin stoi w dobrym miejscu. Jeśli nie — popraw adres powyżej.
                          </p>
                        </div>
                        <p v-else class="text-[11px] text-slate-500">
                          Wpisz adres i miasto — mapka pojawi się automatycznie.
                        </p>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Preferowany termin dostawy*</label>

                        <div class="datePickerWrap">
                          <VueDatePicker
                            v-model="deliveryRange"
                            range
                            auto-apply
                            locale="pl"
                            :format="'dd.MM.yyyy'"
                            :enable-time-picker="false"
                            :clearable="false"
                            :disabled="cancelOrderOnSave"
                            :min-date="todayStartOfDay"
                            :prevent-min-max-navigation="true"
                            placeholder="Wybierz zakres dat dostawy..."
                          />
                        </div>

                        <p class="fieldHint">
                          Wybierz <span class="font-semibold text-slate-200">zakres dat</span>, w którym klient chciałby otrzymać dostawę.
                        </p>
                        <p v-if="errors.deliveryDate" class="fieldError">
                          Wybierz poprawny przedział dat (od – do).
                        </p>
                      </div>
                    </div>
                  </section>

                  <!-- FAKTURA / STATUS -->
                  <section class="sectionCard">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle">Faktura i status</div>
                        <div class="sectionSub">
                          Ustaw dostawcę i początkowy stan realizacji.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody gridGap">
                      <div class="fieldWrap">
                        <label class="fieldLabel">Dostawca*</label>
                        <select
                          v-model="form.invoice.isLauronInvoice"
                          :disabled="cancelOrderOnSave"
                          class="fieldInput"
                        >
                          <option :value="true">Lauron</option>
                          <option :value="false">WBtank</option>
                        </select>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Zamówienie przyjęte do realizacji*</label>
                        <select
                          v-model="form.acceptedByAdmin"
                          :disabled="cancelOrderOnSave"
                          class="fieldInput"
                        >
                          <option :value="false">Nie (wymaga późniejszej akceptacji)</option>
                          <option :value="true">Tak</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <!-- PALIWO -->
                  <section class="sectionCard">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle">Zamówione paliwo</div>
                        <div class="sectionSub">
                          Zmień produkt i ilość.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody gridGap">
                      <div class="fieldWrap">
                        <label class="fieldLabel">Produkt*</label>
                        <select
                          v-model.number="form.orderedFuel.fuelId"
                          :disabled="cancelOrderOnSave"
                          class="fieldInput"
                        >
                          <option :value="null" disabled>Wybierz produkt</option>
                          <option v-for="fuel in fuels" :key="fuel.id" :value="fuel.id">
                            {{ fuel.name }}
                          </option>
                        </select>
                        <p v-if="errors.fuel" class="fieldError">Wybierz produkt paliwowy.</p>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Ilość (L)*</label>
                        <input
                          type="number"
                          min="1"
                          max="99999"
                          step="1"
                          v-model="form.orderedFuel.quantityLiters"
                          :disabled="cancelOrderOnSave"
                          :class="fieldClass(errors.quantityLiters)"
                          placeholder="np. 5000"
                        />
                        <p v-if="errors.quantityLiters" class="fieldError">
                          Podaj ilość w zakresie 1 – 99 999 L.
                        </p>
                      </div>
                    </div>
                  </section>

                  <!-- ANULOWANIE -->
                  <section class="sectionCard sectionDanger">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle text-red-300">Anuluj zamówienie</div>
                        <div class="sectionSub">
                          Oznacz zamówienie jako anulowane.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody">
                      <label class="inline-flex items-start gap-3 cursor-pointer select-none">
                        <input
                          v-model="cancelOrderOnSave"
                          type="checkbox"
                          class="mt-1 h-4 w-4 rounded border-slate-300 text-red-500 focus:ring-red-500"
                        />
                        <span class="text-sm text-slate-100">
                          Chcę anulować to zamówienie – wiem co robię.
                        </span>
                      </label>

                      <p class="fieldHint mt-3" v-if="cancelOrderOnSave">
                        Zamówienie zostanie oznaczone jako
                        <span class="font-semibold text-red-300">anulowane</span>
                        (pole <code>canceled: true</code>).
                      </p>
                    </div>
                  </section>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 shellFooter">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-300">
                      <span v-if="loading">Zapisywanie..</span>
                      <span v-else>
                        Edycja zamówienia paliwowego.
                      </span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        @click.prevent="emitClose"
                        class="btnSecondary"
                        v-if="!loading"
                      >
                        Zamknij
                      </button>

                      <button
                        v-if="!cancelOrderOnSave && !loading"
                        @click.prevent="validateAndSave"
                        class="btnPrimary"
                      >
                        Aktualizuj
                      </button>

                      <button
                        v-if="cancelOrderOnSave && !loading"
                        type="button"
                        @click.prevent="cancelOrder"
                        class="btnDanger"
                      >
                        Anuluj zamówienie
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <ClientInvoicesModal
    v-if="showInvoicesModal"
    :open="showInvoicesModal"
    :invoices="clientInvoices"
    :loading="clientInvoicesLoading"
    :company-name="managedOrder?.client?.invoiceAddress?.name || ''"
    @close="showInvoicesModal = false"
  />
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { db } from '@/firebase/gfbconf.js'
import axios from 'axios'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import ClientInvoicesModal from '@/components/ClientInvoicesModal.vue'
import { computePaymentRating, formatAmountPLN, formatAverageDelay } from '@/utils/paymentRating.js'

export default {
  name: 'ModalManageFuelOrder',
  components: { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot, VueDatePicker, ClientInvoicesModal },
  props: {
    show: { type: Boolean, default: false },
    managedOrder: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      cancelOrderOnSave: false,
      clientSource: 'existing',
      systemUserClientId: null,
      systemClientFromUsersData: null,
      systemUserData: null,
      selectedDeliveryAddressIndex: null,
      useUpdatedSystemClient: false,
      canUpdateClientFromSystem: false,
      clientInvoices: [],
      clientInvoicesLoading: false,
      clientInvoicesUserId: null,
      showInvoicesModal: false,
      regions: [],
      deliveryRange: null,
      originalDeliveryAddress: { address: '', city: '', postCode: '' },
      originalCoordinates: null,
      deliveryVerification: {
        loading: false,
        coordinates: null,
        formattedAddress: null,
        mapEmbedUrl: null,
        error: null,
        verifiedFor: null
      },
      geocodeDebounceTimer: null,
      fuels: [
        { id: 30, name: 'Shell FuelSave Diesel CN 27102011' },
        { id: 33, name: 'Shell V-Power Nitro+ Diesel CN 27102011' },
        { id: 22, name: 'Shell FuelSave 95' },
        { id: 20, name: 'Shell V-Power Nitro+ 95 AL' }
      ],
      form: {
        acceptedByAdmin: true,
        clientManual: {
          contact: {
            email: '',
            invoiceEmail: '',
            phone: ''
          },
          invoiceAddress: {
            address: '',
            city: '',
            name: '',
            nip: '',
            postCode: ''
          }
        },
        deliveryAddress: {
          address: '',
          city: '',
          postCode: '',
          regionName: ''
        },
        invoice: {
          isLauronInvoice: true
        },
        orderedFuel: {
          fuelId: null,
          quantityLiters: null
        }
      },
      errors: {
        clientName: false,
        clientNip: false,
        clientAddress: false,
        clientPostCode: false,
        clientCity: false,
        clientEmail: false,
        clientPhone: false,
        deliveryAddressField: false,
        deliveryPostCode: false,
        deliveryCity: false,
        fuel: false,
        quantityLiters: false,
        deliveryDate: false
      }
    }
  },

  watch: {
    managedOrder: {
      immediate: true,
      async handler(nv) {
        if (!nv) return
        this.loading = false
        this.cancelOrderOnSave = false
        this.useUpdatedSystemClient = false
        this.systemClientFromUsersData = null
        this.canUpdateClientFromSystem = false
        this.clientInvoices = []
        this.clientInvoicesUserId = null
        this.clientInvoicesLoading = false
        this.showInvoicesModal = false

        const sysId = nv.client?.systemUserId || null
        this.systemUserClientId = sysId
        this.clientSource = sysId ? 'existing' : 'manual'

        this.fillFormFromOrder(nv)
        this.buildRangeFromOrder(nv)
        // Jawnie wyzwól geocoding po załadowaniu adresu — watchery czasami nie łapią pierwszego setu w immediate handlerze.
        this.$nextTick(() => { this.scheduleGeocode() })

        if (sysId) {
          await this.loadSystemClient(sysId, nv)
        }
      }
    },
    'form.deliveryAddress.address'() { this.scheduleGeocode() },
    'form.deliveryAddress.city'() { this.scheduleGeocode() },
    'form.deliveryAddress.postCode'() { this.scheduleGeocode() }
  },

  computed: {
    todayStartOfDay() {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d
    },
    clientRating() {
      return computePaymentRating(this.clientInvoices || [], new Date())
    }
  },

  async mounted() {
    await this.loadRegions()
  },

  methods: {
    formatAmountPLN,
    formatAverageDelay,

    fieldClass(hasError) {
      return [
        'fieldInput',
        hasError ? 'fieldInputError' : ''
      ]
    },

    async loadInvoicesForUser(userId) {
      if (!userId) {
        this.clientInvoices = []
        this.clientInvoicesUserId = null
        this.clientInvoicesLoading = false
        return
      }
      if (this.clientInvoicesUserId === userId && !this.clientInvoicesLoading) return

      this.clientInvoicesLoading = true
      this.clientInvoicesUserId = userId
      try {
        const snap = await db.collection('Invoices')
          .where('active', '==', true)
          .where('payerId', '==', userId)
          .get()
        const list = []
        snap.forEach((doc) => {
          const data = doc.data() || {}
          list.push({ id: doc.id, ...data })
        })
        if (this.clientInvoicesUserId === userId) {
          this.clientInvoices = list
          this.clientInvoicesLoading = false
        }
      } catch (e) {
        console.error('Błąd pobierania faktur klienta:', e)
        if (this.clientInvoicesUserId === userId) {
          this.clientInvoices = []
          this.clientInvoicesLoading = false
        }
      }
    },

    async loadRegions() {
      try {
        const doc = await db.collection('routeRegions').doc('regions').get()
        if (doc.exists) {
          const data = doc.data()
          this.regions = Array.isArray(data.regions) ? data.regions : []
        } else {
          this.regions = []
        }
      } catch (e) {
        console.error('Błąd pobierania regionów:', e)
        this.regions = []
      }
    },

    selectDeliveryAddress(index) {
      this.selectedDeliveryAddressIndex = index
      if (index === null) {
        this.form.deliveryAddress.address = ''
        this.form.deliveryAddress.city = ''
        this.form.deliveryAddress.postCode = ''
        this.form.deliveryAddress.regionName = ''
        return
      }
      const addr = this.systemUserData?.deliveryAddresses?.[index]
      if (!addr) return
      this.form.deliveryAddress.address = addr.address || ''
      this.form.deliveryAddress.city = addr.city || ''
      this.form.deliveryAddress.postCode = addr.postCode || ''
      this.form.deliveryAddress.regionName = addr.regionName || ''
    },

    // Po załadowaniu systemUserData próbuje dopasować bieżący adres z form.deliveryAddress do pozycji
    // z listy klienta. Jeśli dopasowanie znalezione — ustawia selectedDeliveryAddressIndex, co skutkuje
    // zablokowaniem inputów i pokazaniem właściwego wpisu w dropdownie.
    autoMatchDeliveryAddressFromList() {
      const list = this.systemUserData?.deliveryAddresses
      if (!Array.isArray(list) || list.length === 0) {
        this.selectedDeliveryAddressIndex = null
        return
      }
      const norm = (v) => (v || '').toString().trim().toLowerCase()
      const curAddr = norm(this.form.deliveryAddress.address)
      const curCity = norm(this.form.deliveryAddress.city)
      const curPost = (this.form.deliveryAddress.postCode || '').toString().trim()
      const idx = list.findIndex(a =>
        norm(a.address) === curAddr &&
        norm(a.city) === curCity &&
        (a.postCode || '').toString().trim() === curPost
      )
      this.selectedDeliveryAddressIndex = idx >= 0 ? idx : null
    },

    fillFormFromOrder(o) {
      if (!o.client?.systemUserId) {
        const c = o.client || {}
        this.form.clientManual = {
          contact: {
            email: c.contact?.email || '',
            invoiceEmail: c.contact?.invoiceEmail || '',
            phone: c.contact?.phone || ''
          },
          invoiceAddress: {
            address: c.invoiceAddress?.address || '',
            city: c.invoiceAddress?.city || '',
            name: c.invoiceAddress?.name || '',
            nip: c.invoiceAddress?.nip || '',
            postCode: c.invoiceAddress?.postCode || ''
          }
        }
      }

      const d = o.deliveryAddress || {}
      this.form.deliveryAddress = {
        address: d.address || '',
        city: d.city || '',
        postCode: d.postCode || '',
        regionName: d.regionName || ''
      }
      this.originalDeliveryAddress = {
        address: d.address || '',
        city: d.city || '',
        postCode: d.postCode || ''
      }
      this.originalCoordinates = d.coordinates && typeof d.coordinates.latitude === 'number' && typeof d.coordinates.longitude === 'number'
        ? { latitude: d.coordinates.latitude, longitude: d.coordinates.longitude }
        : null

      this.form.invoice.isLauronInvoice = !!o.invoice?.isLauronInvoice
      this.form.acceptedByAdmin = !!o.acceptedByAdmin
      this.form.orderedFuel.fuelId = o.orderedFuel?.fuelId ?? null
      this.form.orderedFuel.quantityLiters =
        o.orderedFuel?.quantityLiters != null
          ? Number(o.orderedFuel.quantityLiters)
          : null
    },

    buildRangeFromOrder(o) {
      const fromUnix = o.deliveryDate?.from?.unix
      const toUnix = o.deliveryDate?.to?.unix

      if (
        typeof fromUnix === 'number' &&
        !Number.isNaN(fromUnix) &&
        typeof toUnix === 'number' &&
        !Number.isNaN(toUnix)
      ) {
        this.deliveryRange = [
          new Date(fromUnix * 1000),
          new Date(toUnix * 1000)
        ]
      } else {
        this.deliveryRange = null
      }
    },

    async loadSystemClient(userId, order) {
      try {
        const doc = await db.collection('UsersData').doc(userId).get()
        if (!doc.exists) {
          this.systemClientFromUsersData = null
          this.canUpdateClientFromSystem = false
          return
        }
        const data = doc.data() || {}

        const snapshotFromUsers = {
          contact: {
            email: data.contact?.email || '',
            invoiceEmail: data.contact?.invoiceEmail || '',
            phone: data.contact?.phone || ''
          },
          invoiceAddress: {
            address: data.company?.address || '',
            city: data.company?.city || '',
            name: data.company?.name || '',
            nip: data.company?.nip || '',
            postCode: data.company?.postCode || ''
          },
          systemUserId: userId
        }

        this.systemClientFromUsersData = snapshotFromUsers
        this.systemUserData = data

        // Pobierz historię faktur klienta do wyliczenia ratingu płatniczego.
        this.loadInvoicesForUser(userId)

        // Auto-match bieżącego adresu z zamówienia do listy adresów klienta — żeby dropdown pokazywał właściwy adres zamiast "Wpisz ręcznie".
        this.autoMatchDeliveryAddressFromList()

        const currentClientInOrder = {
          contact: {
            email: order.client?.contact?.email || '',
            invoiceEmail: order.client?.contact?.invoiceEmail || '',
            phone: order.client?.contact?.phone || ''
          },
          invoiceAddress: {
            address: order.client?.invoiceAddress?.address || '',
            city: order.client?.invoiceAddress?.city || '',
            name: order.client?.invoiceAddress?.name || '',
            nip: order.client?.invoiceAddress?.nip || '',
            postCode: order.client?.invoiceAddress?.postCode || ''
          },
          systemUserId: order.client?.systemUserId || null
        }

        // Normalizujemy: trim() + collapse whitespace. Bez tego diff zapalał się
        // przy nieistotnych różnicach (trailing space w NIP, podwójne spacje w nazwie).
        const norm = (v) => (v == null ? '' : String(v)).trim().replace(/\s+/g, ' ')
        const normalize = (obj) => ({
          contact: {
            email: norm(obj.contact?.email),
            invoiceEmail: norm(obj.contact?.invoiceEmail),
            phone: norm(obj.contact?.phone),
          },
          invoiceAddress: {
            address: norm(obj.invoiceAddress?.address),
            city: norm(obj.invoiceAddress?.city),
            name: norm(obj.invoiceAddress?.name),
            // NIP — porównujemy same cyfry, żeby "514-035-7437" === "5140357437".
            nip: String(obj.invoiceAddress?.nip || '').replace(/\D/g, ''),
            postCode: norm(obj.invoiceAddress?.postCode),
          },
          systemUserId: obj.systemUserId || null,
        })
        this.canUpdateClientFromSystem =
          JSON.stringify(normalize(currentClientInOrder)) !== JSON.stringify(normalize(snapshotFromUsers))
      } catch (e) {
        console.error('Błąd pobierania klienta systemowego:', e)
        this.systemClientFromUsersData = null
        this.canUpdateClientFromSystem = false
      }
    },

    toggleUseUpdatedClient() {
      this.useUpdatedSystemClient = !this.useUpdatedSystemClient
    },

    resetErrors() {
      this.errors.clientName = false
      this.errors.clientNip = false
      this.errors.clientAddress = false
      this.errors.clientPostCode = false
      this.errors.clientCity = false
      this.errors.clientEmail = false
      this.errors.clientPhone = false
      this.errors.deliveryAddressField = false
      this.errors.deliveryPostCode = false
      this.errors.deliveryCity = false
      this.errors.fuel = false
      this.errors.quantityLiters = false
      this.errors.deliveryDate = false
    },

    isValidEmail(email) {
      if (!email) return false
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email.trim())
    },

    validate() {
      this.resetErrors()
      let errs = 0

      if (this.clientSource === 'manual') {
        const c = this.form.clientManual

        if (!c.invoiceAddress.name || !c.invoiceAddress.name.trim()) {
          this.errors.clientName = true
          errs++
        }
        if (!c.invoiceAddress.nip || !c.invoiceAddress.nip.trim()) {
          this.errors.clientNip = true
          errs++
        }
        if (!c.invoiceAddress.address || !c.invoiceAddress.address.trim()) {
          this.errors.clientAddress = true
          errs++
        }
        if (!c.invoiceAddress.postCode || !c.invoiceAddress.postCode.trim()) {
          this.errors.clientPostCode = true
          errs++
        }
        if (!c.invoiceAddress.city || !c.invoiceAddress.city.trim()) {
          this.errors.clientCity = true
          errs++
        }
        if (!this.isValidEmail(c.contact.email)) {
          this.errors.clientEmail = true
          errs++
        }
        if (!c.contact.phone || c.contact.phone.trim().length < 5) {
          this.errors.clientPhone = true
          errs++
        }
      }

      const d = this.form.deliveryAddress
      if (!d.address || !d.address.trim()) {
        this.errors.deliveryAddressField = true
        errs++
      }
      if (!d.postCode || !d.postCode.trim()) {
        this.errors.deliveryPostCode = true
        errs++
      }
      if (!d.city || !d.city.trim()) {
        this.errors.deliveryCity = true
        errs++
      }

      if (
        !this.deliveryRange ||
        !Array.isArray(this.deliveryRange) ||
        this.deliveryRange.length !== 2 ||
        !this.deliveryRange[0] ||
        !this.deliveryRange[1]
      ) {
        this.errors.deliveryDate = true
        errs++
      }

      if (!this.form.orderedFuel.fuelId) {
        this.errors.fuel = true
        errs++
      }

      const q = Number(this.form.orderedFuel.quantityLiters)
      if (
        this.form.orderedFuel.quantityLiters === null ||
        this.form.orderedFuel.quantityLiters === '' ||
        !Number.isFinite(q) ||
        q < 1 ||
        q > 99999
      ) {
        this.errors.quantityLiters = true
        errs++
      }

      return errs === 0
    },

    buildDeliveryDate() {
      if (
        !this.deliveryRange ||
        !Array.isArray(this.deliveryRange) ||
        this.deliveryRange.length !== 2
      ) {
        return {
          from: { asText: null, unix: null },
          to: { asText: null, unix: null }
        }
      }

      const [fromRaw, toRaw] = this.deliveryRange
      const from = new Date(fromRaw)
      const to = new Date(toRaw)

      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return {
          from: { asText: null, unix: null },
          to: { asText: null, unix: null }
        }
      }

      const pad = (n) => n.toString().padStart(2, '0')

      const fromDay = pad(from.getDate())
      const fromMonth = pad(from.getMonth() + 1)
      const fromYear = from.getFullYear()
      const fromAsText = `${fromDay}.${fromMonth}.${fromYear}`
      const fromUnix = Math.floor(from.getTime() / 1000)

      const toDay = pad(to.getDate())
      const toMonth = pad(to.getMonth() + 1)
      const toYear = to.getFullYear()
      const toAsText = `${toDay}.${toMonth}.${toYear}`
      const toUnix = Math.floor(to.getTime() / 1000)

      return {
        from: { asText: fromAsText, unix: fromUnix },
        to: { asText: toAsText, unix: toUnix }
      }
    },

    async geocodeDeliveryAddress() {
      const res = await axios.post(`${this.$store.state.apiLink}/geocodeAddress`, {
        address: this.form.deliveryAddress.address,
        city: this.form.deliveryAddress.city,
        postCode: this.form.deliveryAddress.postCode
      })
      return res.data || { success: false, log: 'Brak odpowiedzi z serwera.' }
    },

    addressKey() {
      const d = this.form.deliveryAddress
      return `${(d.address || '').trim().toLowerCase()}|${(d.city || '').trim().toLowerCase()}|${(d.postCode || '').trim()}`
    },

    resetDeliveryVerification() {
      this.deliveryVerification.loading = false
      this.deliveryVerification.coordinates = null
      this.deliveryVerification.formattedAddress = null
      this.deliveryVerification.mapEmbedUrl = null
      this.deliveryVerification.error = null
      this.deliveryVerification.verifiedFor = null
    },

    scheduleGeocode() {
      // Unieważnij poprzedni wynik przy zmianie adresu.
      this.deliveryVerification.coordinates = null
      this.deliveryVerification.formattedAddress = null
      this.deliveryVerification.mapEmbedUrl = null
      this.deliveryVerification.error = null
      this.deliveryVerification.verifiedFor = null

      if (this.geocodeDebounceTimer) clearTimeout(this.geocodeDebounceTimer)
      const d = this.form.deliveryAddress
      if (!(d.address || '').trim() || !(d.city || '').trim()) {
        this.deliveryVerification.loading = false
        return
      }
      this.deliveryVerification.loading = true
      this.geocodeDebounceTimer = setTimeout(() => { this.runGeocodeVerification() }, 700)
    },

    async runGeocodeVerification() {
      const keyAtStart = this.addressKey()
      try {
        const geo = await this.geocodeDeliveryAddress()
        if (this.addressKey() !== keyAtStart) return
        if (!geo.success || !geo.data) {
          this.deliveryVerification.loading = false
          this.deliveryVerification.error = geo.log || 'Nie udało się zweryfikować adresu.'
          this.deliveryVerification.coordinates = null
          this.deliveryVerification.mapEmbedUrl = null
          this.deliveryVerification.formattedAddress = null
          return
        }
        this.deliveryVerification.loading = false
        this.deliveryVerification.error = null
        this.deliveryVerification.coordinates = {
          latitude: geo.data.latitude,
          longitude: geo.data.longitude
        }
        this.deliveryVerification.formattedAddress = geo.data.formattedAddress || null
        this.deliveryVerification.mapEmbedUrl = geo.data.mapEmbedUrl || null
        this.deliveryVerification.verifiedFor = keyAtStart
      } catch (e) {
        if (this.addressKey() !== keyAtStart) return
        console.error('Geocoding error:', e)
        this.deliveryVerification.loading = false
        this.deliveryVerification.error = 'Błąd połączenia z usługą geokodowania.'
      }
    },

    deliveryAddressChanged() {
      const o = this.originalDeliveryAddress || {}
      const n = this.form.deliveryAddress || {}
      return (
        (o.address || '').trim() !== (n.address || '').trim() ||
        (o.city || '').trim() !== (n.city || '').trim() ||
        (o.postCode || '').trim() !== (n.postCode || '').trim()
      )
    },

    async validateAndSave() {
      if (!this.managedOrder?.id && !this.managedOrder?._id) return
      if (!this.validate()) return

      this.loading = true
      const docId = this.managedOrder.id || this.managedOrder._id

      // Jeśli adres się zmienił lub brak koordynatów — zgeokoduj ponownie. SENT-105 wymaga lokalizacji w PL.
      let coordinates = this.originalCoordinates
      const needsGeocode = this.deliveryAddressChanged() || !coordinates
      if (needsGeocode) {
        try {
          // Użyj cache z weryfikacji jeśli dotyczy aktualnego adresu.
          if (this.deliveryVerification.verifiedFor === this.addressKey() && this.deliveryVerification.coordinates) {
            coordinates = {
              latitude: this.deliveryVerification.coordinates.latitude,
              longitude: this.deliveryVerification.coordinates.longitude
            }
          } else {
            const geo = await this.geocodeDeliveryAddress()
            if (!geo.success || !geo.data) {
              this.$store.commit('setNotification', {
                show: true,
                head: 'Nie udało się zweryfikować adresu dostawy',
                subheader: geo.log || 'Sprawdź adres i spróbuj ponownie.',
                success: false
              })
              this.loading = false
              return
            }
            coordinates = {
              latitude: geo.data.latitude,
              longitude: geo.data.longitude
            }
          }
        } catch (e) {
          console.error('Geocoding error:', e)
          this.$store.commit('setNotification', {
            show: true,
            head: 'Błąd połączenia z usługą geokodowania',
            subheader: 'Spróbuj ponownie za chwilę.',
            success: false
          })
          this.loading = false
          return
        }
      }

      try {
        let clientObj
        if (this.clientSource === 'manual') {
          const c = this.form.clientManual
          clientObj = {
            contact: {
              email: c.contact.email || '',
              invoiceEmail: c.contact.invoiceEmail || '',
              phone: c.contact.phone || ''
            },
            invoiceAddress: {
              address: c.invoiceAddress.address || '',
              city: c.invoiceAddress.city || '',
              name: c.invoiceAddress.name || '',
              nip: c.invoiceAddress.nip || '',
              postCode: c.invoiceAddress.postCode || ''
            },
            systemUserId: null
          }
        } else {
          if (this.useUpdatedSystemClient && this.systemClientFromUsersData) {
            clientObj = this.systemClientFromUsersData
          } else {
            clientObj = this.managedOrder.client || null
          }
        }

        const deliveryAddress = {
          address: this.form.deliveryAddress.address || '',
          city: this.form.deliveryAddress.city || '',
          postCode: this.form.deliveryAddress.postCode || '',
          regionName: this.form.deliveryAddress.regionName || null,
          coordinates: coordinates
        }

        const deliveryDate = this.buildDeliveryDate()

        const fuelId = this.form.orderedFuel.fuelId
        const fuel = this.fuels.find((f) => f.id === fuelId)
        const fuelName = fuel ? fuel.name : ''

        const quantityLiters = Number(this.form.orderedFuel.quantityLiters)

        // Aktualizuj status tylko jeśli zamówienie nie jest w trasie (status < 3)
        const currentStatus = this.managedOrder?.status ?? this.managedOrder?.raw?.status
        let newStatus = currentStatus
        if (currentStatus == null || currentStatus < 3) {
          newStatus = this.form.acceptedByAdmin ? 2 : 1
        }

        const payload = {
          status: newStatus,
          client: clientObj,
          deliveryAddress,
          deliveryDate,
          acceptedByAdmin: !!this.form.acceptedByAdmin,
          'invoice.isLauronInvoice': !!this.form.invoice.isLauronInvoice,
          'orderedFuel.fuelId': fuelId,
          'orderedFuel.fuelName': fuelName,
          'orderedFuel.quantityLiters': quantityLiters
        }

        await db.collection('FuelOrders').doc(docId).update(payload)

        // Sync snapshotu zamówienia w aktywnej trasie (jeśli zamówienie jest w trasie).
        try {
          await this.syncOrderSnapshotToRoute(docId, {
            companyName: clientObj?.invoiceAddress?.name || '—',
            deliveryAddress,
            orderedFuel: { fuelId, fuelName, quantityLiters }
          })
        } catch (syncErr) {
          console.error('Błąd synchronizacji snapshotu zamówienia w trasie:', syncErr)
          this.$store.commit('setNotification', {
            show: true,
            head: 'Zamówienie zapisane, ale sync trasy nie powiódł się',
            subheader: 'Snapshot w trasie może być nieaktualny. Skontaktuj się z administratorem.',
            success: false
          })
        }

        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona sukcesem!',
          subheader: 'Zamówienie paliwowe zostało zaktualizowane.',
          success: true
        })

        this.$emit('reloadOrders', docId)
        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu do bazy',
          subheader: 'Spróbuj ponownie lub skontaktuj się z administratorem.',
          success: false
        })
        this.loading = false
      }
    },

    async syncOrderSnapshotToRoute(orderId, updates) {
      // Znajdź aktywną trasę zawierającą to zamówienie (status 1 lub 2) i podmień jego snapshot w tablicy `orders`.
      // Wykorzystujemy transakcję, bo edytujemy element w tablicy obiektów (Firestore nie ma partial update dla array element).
      const orderRef = db.collection('FuelOrders').doc(orderId)
      const snap = await db.collection('Routes')
        .where('status', 'in', [1, 2])
        .get()

      // Filtrujemy po stronie klienta — Firestore nie przeszuka array of objects po `orders[].ref == orderRef`.
      const matching = snap.docs.filter(d => {
        const orders = d.data()?.orders
        if (!Array.isArray(orders)) return false
        return orders.some(o => o && o.ref && typeof o.ref.isEqual === 'function' && o.ref.isEqual(orderRef))
      })

      if (matching.length === 0) return

      await Promise.all(matching.map(async (doc) => {
        await db.runTransaction(async (tx) => {
          const fresh = await tx.get(doc.ref)
          if (!fresh.exists) return
          const data = fresh.data() || {}
          const orders = Array.isArray(data.orders) ? data.orders : []

          const newOrders = orders.map(o => {
            if (!o || !o.ref || typeof o.ref.isEqual !== 'function') return o
            if (!o.ref.isEqual(orderRef)) return o

            const prevSnap = o.snapshotAtAddingTime || {}
            return {
              ...o,
              snapshotAtAddingTime: {
                ...prevSnap,
                companyName: updates.companyName || prevSnap.companyName || '—',
                deliveryAddress: {
                  address: updates.deliveryAddress?.address || '—',
                  city: updates.deliveryAddress?.city || '—',
                  postCode: updates.deliveryAddress?.postCode || '—',
                  regionName: updates.deliveryAddress?.regionName || 'Nie przypisano',
                  coordinates: updates.deliveryAddress?.coordinates || prevSnap.deliveryAddress?.coordinates || null
                },
                orderedFuel: {
                  fuelId: updates.orderedFuel?.fuelId || '—',
                  fuelName: updates.orderedFuel?.fuelName || '—',
                  quantityLiters: updates.orderedFuel?.quantityLiters ?? '—'
                }
              }
            }
          })

          const allRegions = [...new Set(
            newOrders
              .map(o => o?.snapshotAtAddingTime?.deliveryAddress?.regionName)
              .filter(Boolean)
          )]

          tx.update(doc.ref, { orders: newOrders, allRegions })
        })
      }))
    },

    async cancelOrder() {
      if (!this.managedOrder?.id && !this.managedOrder?._id) return
      this.loading = true
      const docId = this.managedOrder.id || this.managedOrder._id

      try {
        await db.collection('FuelOrders').doc(docId).update({ canceled: true, status: 6 })

        this.$store.commit('setNotification', {
          show: true,
          head: 'Zamówienie anulowane!',
          subheader: 'Zamówienie zostało oznaczone jako anulowane.',
          success: true
        })

        this.$emit('reloadOrders', docId)
        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd podczas anulowania',
          subheader: 'Nie udało się anulować zamówienia.',
          success: false
        })
        this.loading = false
      }
    },

    emitClose() {
      this.$emit('closeOrderModal')
      this.loading = false
      this.cancelOrderOnSave = false
      this.resetErrors()
      this.clientInvoices = []
      this.clientInvoicesUserId = null
      this.clientInvoicesLoading = false
      this.showInvoicesModal = false
    }
  }
}
</script>

<style scoped>
.backdrop-soft {
  backdrop-filter: blur(3px);
}

.shellPanel {
  height: 100%;
  background: #1e293b;
  border-left: 1px solid rgba(71, 85, 105, 0.9);
  box-shadow: -10px 0 35px rgba(0, 0, 0, 0.28);
}

.shellHeader {
  padding: 20px 20px 18px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.7);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.96));
}

.shellBody {
  padding: 16px;
  background: rgba(30, 41, 59, 0.98);
}

.shellFooter {
  padding: 14px 16px;
  border-top: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(15, 23, 42, 0.92);
}

.sectionCard {
  overflow: visible;
  position: relative;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.sectionDanger {
  border-color: rgba(127, 29, 29, 0.7);
  background: rgba(69, 10, 10, 0.18);
}

.sectionHead {
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.65);
}

.sectionTitle {
  font-size: 15px;
  line-height: 1.3;
  font-weight: 700;
  color: #f8fafc;
}

.sectionSub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
}

.sectionBody {
  padding: 16px 18px 18px;
}

.gridGap > * + * {
  margin-top: 14px;
}

.headerPill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 9999px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(51, 65, 85, 0.7);
  font-size: 11px;
  line-height: 1;
  color: #cbd5e1;
}

.headerPillBlue {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(37, 99, 235, 0.18);
  color: #bfdbfe;
}

.infoBox {
  border: 1px solid rgba(71, 85, 105, 0.7);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.45);
  padding: 12px 14px;
}

.infoLabel {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

.infoValue {
  margin-top: 5px;
  font-size: 13px;
  font-weight: 700;
  color: #f8fafc;
}

.dataPreview {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.previewBlock {
  border: 1px solid rgba(71, 85, 105, 0.65);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.35);
  padding: 14px;
}

.previewLabel {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
}

.previewMain {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #f8fafc;
}

.previewText {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #e2e8f0;
  word-break: break-word;
}

.fieldWrap {
  display: block;
}

.fieldLabel {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldInput {
  display: block;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.fieldInput:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.fieldInput:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fieldInputError {
  border-color: rgba(239, 68, 68, 0.9) !important;
}

.addrVerifyBox {
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid rgba(100, 116, 139, 0.45);
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
}

.addrVerifyLoading {
  color: #cbd5e1;
}

.addrVerifyError {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(127, 29, 29, 0.25);
  color: #fecaca;
}

.addrVerifySuccess {
  border-color: rgba(16, 185, 129, 0.45);
  background: rgba(6, 78, 59, 0.25);
}

.fieldHint {
  margin-top: 7px;
  font-size: 11px;
  line-height: 1.45;
  color: #94a3b8;
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #fca5a5;
}

.iconBtn {
  height: 40px;
  width: 40px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  transition: 180ms ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary,
.btnDanger {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  transition: 180ms ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.5);
  background: rgba(37, 99, 235, 0.2);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.btnDanger {
  border: 1px solid rgba(239, 68, 68, 0.55);
  background: rgba(220, 38, 38, 0.2);
  color: #fecaca;
}

.btnDanger:hover {
  background: rgba(220, 38, 38, 0.3);
}

.pickedFromListHint {
  font-size: 10px;
  line-height: 1.2;
  color: rgba(148, 163, 184, 0.9);
  font-style: italic;
}

.ratingCard {
  border: 1px solid rgba(71, 85, 105, 0.75);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.45);
  padding: 14px 16px;
}

.ratingTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ratingHeading {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #ffffff;
  text-transform: uppercase;
}

.ratingSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.45;
  color: rgba(148, 163, 184, 1);
}

.ratingBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.ratingBtn:hover {
  background: rgba(37, 99, 235, 0.3);
}

.ratingBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ratingStarsRow {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.starsWrap {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.starIcon {
  height: 20px;
  width: 20px;
  fill: rgba(71, 85, 105, 0.95);
  transition: fill 0.18s ease;
}

.starIcon--on {
  fill: #facc15;
}

.starsWrap--empty .starIcon {
  fill: rgba(51, 65, 85, 0.85);
}

.ratingValue {
  font-size: 13px;
  font-weight: 700;
  color: #f8fafc;
}

.ratingValueHint {
  margin-left: 4px;
  font-weight: 500;
  color: rgba(148, 163, 184, 1);
}

.ratingGrid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

@media (min-width: 520px) {
  .ratingGrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.metricBox {
  border: 1px solid rgba(71, 85, 105, 0.55);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.55);
  padding: 10px 12px;
}

.metricLabel {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(148, 163, 184, 1);
}

.metricValue {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
}

.metricValue--danger {
  color: #fca5a5;
}

.metricSub {
  margin-top: 4px;
  font-size: 10px;
  color: rgba(148, 163, 184, 0.95);
}

/* DATEPICKER — skopiowane z RouteCreate, pełny zestaw reguł łapiący wszystkie stany */
.datePickerWrap {
  position: relative;
  z-index: 80;
}

:deep(.dp__main) {
  width: 100%;
}

:deep(.dp__input_wrap) {
  width: 100%;
}

:deep(.dp__input) {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 36px 11px 38px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}

:deep(.dp__input::placeholder) {
  color: rgba(148, 163, 184, 0.75);
}

:deep(.dp__input_focus) {
  border-color: rgba(99, 102, 241, 0.85) !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
}

:deep(.dp__icon),
:deep(.dp__clear_icon) {
  color: rgba(148, 163, 184, 0.9);
}

:deep(.dp__menu) {
  z-index: 120 !important;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.98) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

:deep(.dp__menu_inner),
:deep(.dp__instance_calendar),
:deep(.dp__calendar),
:deep(.dp__calendar_wrap),
:deep(.dp__overlay),
:deep(.dp__selection_preview),
:deep(.dp__time_picker),
:deep(.dp__time_picker_inline_container),
:deep(.dp__time_picker_overlay_container),
:deep(.dp__time_picker_overlay),
:deep(.dp__action_row),
:deep(.dp__month_year_row),
:deep(.dp__calendar_header),
:deep(.dp__calendar_header_item),
:deep(.dp__month_year_wrap) {
  background: rgba(15, 23, 42, 0.98) !important;
  color: #f8fafc !important;
}

:deep(.dp__arrow_top) {
  display: none !important;
}

:deep(.dp__month_year_select) {
  color: #f8fafc !important;
}

:deep(.dp__btn),
:deep(.dp__icon_btn),
:deep(.dp__inner_nav),
:deep(.dp__inc_dec_button),
:deep(.dp__pm_am_button),
:deep(.dp__overlay_action),
:deep(.dp__tp_inline_btn),
:deep([data-test="tp-btn"]),
:deep(.dp__time_picker_inline_btn) {
  color: #cbd5e1 !important;
  background: transparent !important;
  border: none !important;
}

:deep(.dp__btn:hover),
:deep(.dp__icon_btn:hover),
:deep(.dp__inner_nav:hover),
:deep(.dp__inc_dec_button:hover),
:deep(.dp__pm_am_button:hover),
:deep(.dp__overlay_action:hover),
:deep(.dp__tp_inline_btn:hover),
:deep([data-test="tp-btn"]:hover),
:deep(.dp__time_picker_inline_btn:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
  border: none !important;
}

:deep(.dp__week_day) {
  color: rgba(148, 163, 184, 1) !important;
  font-weight: 600;
}

:deep(.dp__calendar_item) {
  color: #e2e8f0 !important;
}

:deep(.dp__cell_inner) {
  border-radius: 10px !important;
  color: inherit !important;
  box-shadow: none !important;
  border: 0 !important;
}

:deep(.dp__cell_inner:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__range_start),
:deep(.dp__range_end),
:deep(.dp__active_date) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between) {
  background: rgba(37, 99, 235, 0.18) !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between .dp__cell_inner) {
  background: transparent !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

:deep(.dp__range_between:hover),
:deep(.dp__range_between:hover .dp__cell_inner),
:deep(.dp__range_between.dp__date_hover),
:deep(.dp__range_between.dp__date_hover .dp__cell_inner) {
  background: rgba(37, 99, 235, 0.24) !important;
  color: #ffffff !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__today) {
  border: 1px solid rgba(59, 130, 246, 0.65) !important;
}

:deep(.dp__cell_disabled),
:deep(.dp__disabled),
:deep(.dp__disabled .dp__cell_inner),
:deep(.dp__calendar_item.dp__cell_disabled .dp__cell_inner) {
  color: rgba(100, 116, 139, 0.6) !important;
  opacity: 0.45 !important;
  cursor: not-allowed !important;
}

:deep(.dp__time_display),
:deep(.dp__time_col),
:deep(.dp__time_input),
:deep(.dp__time_input input),
:deep(.dp__overlay_cell),
:deep(.dp__overlay_col),
:deep(.dp__overlay_cell_active),
:deep(.dp__overlay_cell_pad),
:deep(.dp__time_picker_item),
:deep(.dp__time_col_reg),
:deep(.dp__time_col_reg_block),
:deep(.dp__time_col_reg_inline) {
  color: #f8fafc !important;
  background: transparent !important;
}

:deep(.dp__time_display) {
  font-weight: 700 !important;
}

:deep(.dp__overlay_cell:hover),
:deep(.dp__overlay_col:hover),
:deep(.dp__time_display:hover),
:deep(.dp__time_col_reg_block:hover),
:deep(.dp__time_col_reg_inline:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__action_row) {
  border-top: 1px solid rgba(71, 85, 105, 0.7) !important;
}

:deep(.dp__action_button) {
  border-radius: 12px !important;
}

:deep(.dp__action_select) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
}

:deep(.dp__action_cancel) {
  color: #cbd5e1 !important;
  background: transparent !important;
}

:deep(.dp__action_cancel:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__selection_preview) {
  color: #94a3b8 !important;
}

:deep(.dp__menu *:hover) {
  color: inherit;
}

:deep(.dp__menu button:hover),
:deep(.dp__menu [role="button"]:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

@media (min-width: 640px) {
  .shellHeader {
    padding: 24px 24px 20px;
  }

  .shellBody {
    padding: 18px;
  }

  .shellFooter {
    padding: 16px 18px;
  }

  .dataPreview {
    grid-template-columns: 1fr 1fr;
  }
}
</style>